/* eslint-disable react-hooks/exhaustive-deps */
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, SvgIcon, Typography } from '@mui/material';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { Button, TextFieldWrapper } from '@Components/index';
import Modal from '@Components/Modal';
import Icons from '@Constants/Icons.constants';
import {
  useSubmitVerificationCodeTelnyxMutation,
  useVerifyCallerTelnyxMutation,
} from '@Services/userCallerIds.service';
import { BaseAlertProps } from '@Typings/global.types';
import { unformatNumber } from '@Utils/common/common.utils';
import useAlert from '@Utils/hooks/useAlert.hook';

import {
  SmsCodeVerificationModalInputs,
  SmsCodeVerificationModalProps,
} from './SmsCodeVerificationModal.types';

const SmsCodeVerificationModal: FunctionComponent<SmsCodeVerificationModalProps> =
  (props: SmsCodeVerificationModalProps): JSX.Element => {
    const {
      isOpen,
      setIsOpen,
      setDisableVerifyButton,
      phone,
      setIsCallerIdInvalidated,
      isTpr,
    } = props;

    const { t } = useTranslation();
    const { triggerAlert } = useAlert();

    const [isCodeRetriesExceeded, setIsCodeRetriesExceeded] =
      useState<boolean>(false);
    const [isWrongCode, setIsWrongCode] = useState<boolean>(false);
    const [isSMSRetriesExceeded, setIsSMSRetriesExceeded] =
      useState<boolean>(false);
    const [isSuccessfullyValidated, setIsSuccessfullyValidated] =
      useState<boolean>(false);
    const [alertState, setAlertState] = useState<BaseAlertProps>({
      openAlert: false,
    });

    const [requestSms, requestSmsMethods] = useVerifyCallerTelnyxMutation();

    const [submitVerificationCode, submitVerificationCodeMethods] =
      useSubmitVerificationCodeTelnyxMutation();

    const isLoading =
      requestSmsMethods.isLoading || submitVerificationCodeMethods.isLoading;

    const pinValidationSchema = (): yup.AnyObjectSchema => {
      const requiredMessage = t('common.formErrorMessages.required');
      const lengthMessage = t('common.formErrorMessages.code_5_length_error');

      return yup
        .object({
          pin: yup.string().required(requiredMessage).length(5, lengthMessage),
        })
        .required();
    };

    const methods = useForm<SmsCodeVerificationModalInputs>({
      resolver: yupResolver(pinValidationSchema()),
      mode: 'onChange',
      shouldUnregister: true,
    });

    /**
     * Sends pin for validation
     */
    const onSubmitHandler: SubmitHandler<SmsCodeVerificationModalInputs> = (
      formData
    ) => {
      if (formData) {
        setIsCodeRetriesExceeded(false);

        return submitVerificationCode({
          phone: unformatNumber(phone),
          verificationCode: formData.pin,
          isTpr,
        });
      }
    };

    const sendSms = () => {
      setIsCodeRetriesExceeded(false);
      return requestSms({ phone: unformatNumber(phone) });
    };

    const closeModal = () => {
      if (isSuccessfullyValidated) {
        setIsCallerIdInvalidated(true);
      }

      setIsOpen(false);
      setIsCodeRetriesExceeded(false);
      setIsSMSRetriesExceeded(false);
      setIsSuccessfullyValidated(false);
      setDisableVerifyButton(false);
    };

    /**
     * Error and Success alerts for pin validation
     */
    useEffect(() => {
      if (submitVerificationCodeMethods.error) {
        triggerAlert(submitVerificationCodeMethods.error, setAlertState);
      }
      if (submitVerificationCodeMethods.isSuccess) {
        setIsWrongCode(false);

        if (!submitVerificationCodeMethods?.data?.data?.verifiedAt) {
          setIsWrongCode(true);
        } else if (submitVerificationCodeMethods.data.data.verifiedAt) {
          setIsSuccessfullyValidated(true);
          setIsSMSRetriesExceeded(false);
          setIsCodeRetriesExceeded(false);
        }
      }
    }, [
      submitVerificationCodeMethods.isSuccess,
      submitVerificationCodeMethods.error,
      submitVerificationCodeMethods.data,
      triggerAlert,
      submitVerificationCodeMethods,
      setIsOpen,
    ]);

    useEffect(() => {
      if (isSuccessfullyValidated) {
        setTimeout(() => {
          closeModal();
        }, 2000);
      }
    }, [isSuccessfullyValidated]);

    /**
     * Error and Success alerts for sending sms with pin
     */
    useEffect(() => {
      if (requestSmsMethods.error) {
        triggerAlert(requestSmsMethods.error, setAlertState);
      }
      if (requestSmsMethods.isSuccess) {
        setIsWrongCode(false);
        if (requestSmsMethods.data.message === 'SMS_AMOUNT_EXCEEDED') {
          setIsSMSRetriesExceeded(true);
        } else {
          setIsSMSRetriesExceeded(false);
        }
      }
    }, [
      requestSmsMethods.isSuccess,
      requestSmsMethods.error,
      requestSmsMethods.data,
      triggerAlert,
      requestSmsMethods,
    ]);

    return (
      <>
        <Modal
          open={isOpen}
          headerText={t('userCallerIds.Sms Verification')}
          onClose={() => closeModal()}
          alertState={alertState}
        >
          <FormProvider {...methods}>
            <Box
              component="form"
              onSubmit={methods.handleSubmit(onSubmitHandler)}
            >
              <>
                <Box
                  display="flex"
                  flexDirection="column"
                  justifyContent="center"
                  sx={{ padding: '3rem 2.4rem 2.4rem' }}
                >
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    mt={0}
                  >
                    {isSuccessfullyValidated && (
                      <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        mb={4}
                      >
                        <SvgIcon
                          sx={{
                            width: 33.3,
                            height: 33.3,
                            mb: 1.4,
                            color: '#178549',
                          }}
                        >
                          {Icons.checkCircle}
                        </SvgIcon>
                        <Typography fontSize="18px" fontWeight="600">
                          {`${t(
                            'userCallerIds.Caller Id Successfully Confirmed'
                          )}`}
                        </Typography>
                      </Box>
                    )}
                    {!isSuccessfullyValidated && (
                      <>
                        <Box mb={2}>
                          {isCodeRetriesExceeded && (
                            <Typography
                              variant="h2"
                              fontWeight={500}
                              textAlign="center"
                              sx={{ color: '#a90000' }}
                            >
                              {t('userCallerIds.Pin Retries Exceeded')}
                            </Typography>
                          )}
                          {isSMSRetriesExceeded && (
                            <Typography
                              variant="h2"
                              fontWeight={500}
                              textAlign="center"
                              sx={{ color: '#a90000' }}
                            >
                              {t('userCallerIds.SMS Retries Exceeded')}
                            </Typography>
                          )}
                          {isWrongCode && (
                            <Typography
                              variant="h2"
                              fontWeight={500}
                              textAlign="center"
                              sx={{ color: '#a90000' }}
                            >
                              {t('userCallerIds.Wrong Pin')}
                            </Typography>
                          )}
                          {!isCodeRetriesExceeded &&
                            !isWrongCode &&
                            !isSMSRetriesExceeded && (
                              <Typography variant="h2" textAlign="center">
                                {t('userCallerIds.Pin 5 Message')}
                              </Typography>
                            )}
                        </Box>
                        <Box display="flex" gap={3}>
                          <TextFieldWrapper
                            name="pin"
                            placeholder={`${t('userCallerIds.Enter code')} *`}
                            disabled={
                              isCodeRetriesExceeded || isSMSRetriesExceeded
                            }
                          />
                          <Button
                            color="primary"
                            disabled={isLoading || isSMSRetriesExceeded}
                            onClick={() => sendSms()}
                          >
                            {t('common.messages.Resend SMS')}
                          </Button>
                        </Box>
                      </>
                    )}
                  </Box>
                </Box>
                {!isSuccessfullyValidated && (
                  <Box
                    display="flex"
                    flexDirection="column"
                    sx={{
                      borderTop: '0.1rem solid #A3ABB2',
                      padding: '3rem 2.4rem 2.4rem',
                    }}
                  >
                    <Box display="flex" justifyContent="flex-end">
                      <Button
                        onClick={() => closeModal()}
                        color="secondary"
                        variant="outlined"
                        sx={{ ml: 'auto', mr: 1.6 }}
                      >
                        {t('common.messages.Cancel')}
                      </Button>
                      <Button
                        type="submit"
                        color="primary"
                        disabled={
                          isLoading ||
                          isSMSRetriesExceeded ||
                          isCodeRetriesExceeded
                        }
                      >
                        {t('common.messages.Submit')}
                      </Button>
                    </Box>
                  </Box>
                )}
              </>
            </Box>
          </FormProvider>
        </Modal>
      </>
    );
  };

export default SmsCodeVerificationModal;
